import { AnnotatedJsonSchema } from '@web-config-app/core';
import { useTranslate } from '@web-config-app/core-react';
import React from 'react';

/**
 * Check if the current array schema has the flowNodes control annotation.
 * if it does, we will render the flow viewer button and the flow node array item component.
 */
const isFlowNodeControl = (schema: AnnotatedJsonSchema) => {
  const controlType = schema?.['x-entity-control']?.type;
  return controlType === 'flowNodes';
};

const findNodeByType = (
  nodeTypeValue: string,
  nodeTypeKey: string,
  schemas: any[],
): any | undefined =>
  schemas.find(
    (obj) =>
      obj.properties &&
      obj.properties[nodeTypeKey] &&
      obj.properties[nodeTypeKey].enum &&
      obj.properties[nodeTypeKey].enum.includes(nodeTypeValue),
  );

export const useFlowArray = (schema: AnnotatedJsonSchema) => {
  const translate = useTranslate();

  const isFlowNodeControlAnnotation = isFlowNodeControl(schema);

  // property where the nodeType is defined
  const nodeType =
    schema?.['x-entity-control']?.options?.flow?.nodeType || 'nodeType';

  // Derive nodeTypeLabelMap from the items schema oneOf definitions when this is a flowNodes control
  const nodeTypeLabelMap = React.useMemo(() => {
    if (!isFlowNodeControlAnnotation) return undefined;
    const oneOf = schema.oneOf || [];

    const actionNode = findNodeByType('action', nodeType, oneOf);
    const decisionNode = findNodeByType('decision', nodeType, oneOf);

    const actionLabel =
      actionNode?.['x-entity-label']?.text ??
      (actionNode?.['x-entity-label']?.key
        ? translate(actionNode['x-entity-label'].key)
        : translate('ACTION'));

    const decisionLabel =
      decisionNode?.['x-entity-label']?.text ??
      (decisionNode?.['x-entity-label']?.key
        ? translate(decisionNode['x-entity-label'].key)
        : translate('DECISION'));

    return {
      action: actionLabel,
      decision: decisionLabel,
    };
  }, [isFlowNodeControlAnnotation, nodeType, schema.oneOf, translate]);

  // derive next key map from schema annotation if present
  const nodeNextKeyMap = React.useMemo(() => {
    const mapping = (schema as AnnotatedJsonSchema)?.['x-entity-control']
      ?.options?.flow?.nodeOptions;
    return {
      action: mapping?.action?.next || 'next',
      decision: mapping?.decision?.next || 'next',
      start: mapping?.start?.next || 'next',
    };
  }, [schema]);

  const addStartNode = React.useMemo(() => {
    const options = (schema as AnnotatedJsonSchema)?.['x-entity-control']
      ?.options?.flow?.nodeOptions;

    return Boolean(options?.start?.addVirtual);
  }, [schema]);

  const addEndNode = React.useMemo(() => {
    const options = (schema as AnnotatedJsonSchema)?.['x-entity-control']
      ?.options?.flow?.nodeOptions;
    return Boolean(options?.end?.addVirtual);
  }, [schema]);

  return {
    isFlowNodeControlAnnotation,
    nodeTypeLabelMap,
    nodeNextKeyMap,
    addStartNode,
    addEndNode,
  };
};
